import React from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { GcpProjectPickerFieldExtension } from './scaffolder/GcpProjectPickerExtension';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { 
  techdocsPlugin,
  TechDocsIndexPage,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SidebarPage,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { Root } from './modules/nav/Sidebar';
import { sauterTheme } from './theme/sauterTheme';
import { mouraTheme } from './theme/mouraTheme';
import { UnifiedThemeProvider } from '@backstage/theme';
import CssBaseline from '@material-ui/core/CssBaseline';
import { configApiRef, createApiFactory, googleAuthApiRef } from '@backstage/core-plugin-api';
import { scmIntegrationsApiRef, ScmIntegrationsApi, ScmAuth } from '@backstage/integration-react';
import { TechRadarPage, techRadarApiRef } from '@backstage/plugin-tech-radar';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';

// Catalog e Observabilidade
import { entityPage } from './components/catalog/EntityPage';

const app = createApp({
  apis: [
    createApiFactory({
      api: scmIntegrationsApiRef,
      deps: { configApi: configApiRef },
      factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
    }),
    createApiFactory({
      api: techRadarApiRef,
      deps: { configApi: configApiRef },
      factory: ({ configApi }) => ({
        load: async () => {
          const response = await fetch(`${configApi.getString('app.baseUrl')}/tech-radar.json`);
          return response.json();
        },
      }),
    }),
    ScmAuth.createDefaultApiFactory(),
  ],
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        provider={{
          id: 'google',
          title: 'Google',
          message: 'Sign in using Google',
          apiRef: googleAuthApiRef,
        }}
      />
    ),
  },
  themes: [
    {
      id: 'sauter-light',
      title: 'Sauter Light',
      variant: 'light',
      Provider: ({ children }: { children: React.ReactNode }) => (
        <UnifiedThemeProvider theme={sauterTheme} children={
          <>
            <CssBaseline />
            {children}
          </>
        } />
      ),
    },
    {
      id: 'moura-theme',
      title: 'Grupo Moura',
      variant: 'light',
      Provider: ({ children }: { children: React.ReactNode }) => (
        <UnifiedThemeProvider theme={mouraTheme} children={
          <>
            <CssBaseline />
            {children}
          </>
        } />
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <SidebarPage>
        <Root />
        <FlatRoutes>
          <Route path="/" element={<HomepageCompositionRoot><HomePage /></HomepageCompositionRoot>} />
          <Route path="/catalog" element={<CatalogIndexPage />} />
          <Route
            path="/catalog/:namespace/:kind/:name/*"
            element={<CatalogEntityPage />}
          >
            {entityPage}
          </Route>
          <Route path="/docs" element={<TechDocsIndexPage />} />
          <Route
            path="/docs/:namespace/:kind/:name/*"
            element={<TechDocsReaderPage />}
          >
            <TechDocsAddons />
          </Route>
          <Route path="/create" element={<ScaffolderPage headerOptions={{ title: 'Create', pageTitleOverride: 'Create a new component' }} />}>
            <ScaffolderFieldExtensions>
              <GcpProjectPickerFieldExtension />
            </ScaffolderFieldExtensions>
          </Route>
          <Route path="/api-docs" element={<ApiExplorerPage />} />
          <Route path="/catalog-graph" element={<CatalogGraphPage />} />
          <Route path="/catalog-import" element={<CatalogImportPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/settings" element={<UserSettingsPage />} />
          <Route
            path="/tech-radar"
            element={<TechRadarPage width={1500} height={800} />}
          />
        </FlatRoutes>
      </SidebarPage>
    </AppRouter>
  </>
);
